import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { CreditCard, RefreshCw, Replace, Undo2, Save, Loader2 } from "lucide-react";
import { API_BASE } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "./ui/alert-dialog";

const call = async (path, method = "GET", body) => {
  const r = await fetch(`${API_BASE}${path}`, {
    method, credentials: "include",
    headers: body ? { "Content-Type": "application/json" } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.display_message || (typeof json.detail === "string" ? json.detail : "Please check the values you entered."));
  return json;
};

function PricingSettings() {
  const [prices, setPrices] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    call("/admin/settings/pricing").then(setPrices).catch(() => {});
  }, []);
  if (!prices) return null;
  const field = (k, label, step = "0.01", min = "0.5") => (
    <label className="text-xs font-semibold text-slate-600 space-y-1">
      <span>{label}</span>
      <Input data-testid={`price-input-${k}`} type="number" step={step} min={min} value={prices[k]} onChange={(e) => setPrices({ ...prices, [k]: e.target.value })} className="h-10 bg-white" />
    </label>
  );
  const save = async () => {
    if (Object.values(prices).some((v) => v === "" || Number.isNaN(Number(v)))) {
      toast.error("Please fill in every price and limit.");
      return;
    }
    if (!Number.isInteger(Number(prices.max_free_diagnostics))) {
      toast.error("Free diagnoses must be a whole number.");
      return;
    }
    setSaving(true);
    try {
      setPrices(await call("/admin/settings/pricing", "PUT", Object.fromEntries(Object.entries(prices).map(([k, v]) => [k, Number(v)]))));
      toast.success("Prices saved");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };
  return (
    <div data-testid="admin-pricing" className="rounded-2xl bg-white border border-slate-200 p-4">
      <div className="font-display font-bold text-[#092C15] flex items-center gap-2"><CreditCard size={16} /> Landscape Vision Pricing (one-time, USD)</div>
      <p className="text-xs text-slate-500 mt-0.5">First concept is always free. Changes apply to new checkouts.</p>
      <div className="mt-3 grid gap-3 sm:grid-cols-4 items-end">
        {field("extra_view", "Additional view")}
        {field("whole_property", "Whole property (4 views)")}
        {field("revision", "Revised concept")}
      </div>
      <div data-testid="admin-diagnostic-limits" className="border-t border-slate-200 mt-4 pt-4">
        <div className="font-display font-bold text-[#092C15]">Diagnose My Landscape Limits</div>
        <p className="text-xs text-slate-500 mt-0.5">Free complete diagnoses per customer email (written assessment + Ask Dr. Verdant follow-up count as one). Each diagnosis after that is charged the fee below.</p>
        <div className="mt-3 grid gap-3 sm:grid-cols-4">
          {field("max_free_diagnostics", "Free diagnoses per customer", "1", "0")}
          {field("diagnostic", "Overage fee (USD)")}
          <div className="flex items-end">
            <Button data-testid="save-pricing-button" onClick={save} disabled={saving} className="h-10 w-full bg-[#092C15] hover:bg-emerald-900">
              {saving ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Save size={14} className="mr-1.5" />} Save settings
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Purchase({ p, reload }) {
  const [busy, setBusy] = useState("");
  const act = async (key, path, msg) => {
    setBusy(key);
    try {
      await call(path, "POST");
      toast.success(msg);
      reload();
    } catch (e) {
      toast.error(e.message);
      reload();
    } finally {
      setBusy("");
    }
  };
  return (
    <div data-testid={`admin-purchase-${p.id}`} className="rounded-xl border border-slate-200 bg-white p-3 text-sm">
      <div className="flex flex-wrap justify-between gap-2">
        <div><strong>{p.customer?.name}</strong> · {p.label} · {p.admin_comp ? "Admin — free" : `$${Number(p.amount).toFixed(2)}`}</div>
        <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${p.status === "paid" ? "bg-emerald-100 text-emerald-800" : p.status === "needs_refund" || p.partially_fulfilled ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-700"}`}>{p.status === "needs_refund" ? "needs refund" : p.status}{p.partially_fulfilled ? " · partial" : ""}</span>
      </div>
      <div className="text-xs text-slate-500 mt-0.5 break-all">Payment ref: {p.payment_reference || p.stripe_session_id} · {p.created_at?.slice(0, 16).replace("T", " ")} UTC</div>
      <div className="mt-2 space-y-1.5">
        {(p.concepts || []).map((c) => (
          <div key={c.id} className="flex flex-wrap items-center gap-2">
            <span className="text-xs">v{c.version} — <strong>{c.status}</strong>{c.error ? <span className="text-rose-700"> ({c.error.slice(0, 120)})</span> : null}</span>
            {c.status === "complete" && <a className="text-xs underline text-sky-700" href={`${API_BASE}/concepts/${c.id}`} target="_blank" rel="noreferrer">view</a>}
            {c.status === "error" && (
              <Button size="sm" variant="outline" data-testid={`admin-retry-${c.id}`} disabled={!!busy} onClick={() => act(c.id + "r", `/admin/concepts/${c.id}/retry`, "Retry started")} className="h-7 rounded-full text-xs">
                <RefreshCw size={12} className="mr-1" /> Retry
              </Button>
            )}
            {["complete", "error"].includes(c.status) && p.status !== "refunded" && (
              <Button size="sm" variant="outline" data-testid={`admin-replace-${c.id}`} disabled={!!busy} onClick={() => act(c.id + "p", `/admin/concepts/${c.id}/replace`, "Replacement started — previous version kept")} className="h-7 rounded-full text-xs">
                <Replace size={12} className="mr-1" /> Replace
              </Button>
            )}
          </div>
        ))}
      </div>
      {["paid", "needs_refund"].includes(p.status) && !p.admin_comp && (
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button size="sm" variant="outline" data-testid={`admin-refund-${p.id}`} disabled={!!busy}
              className="mt-2 h-8 rounded-full text-xs border-rose-300 text-rose-700">
              {busy === "refund" ? <Loader2 size={12} className="mr-1 animate-spin" /> : <Undo2 size={12} className="mr-1" />} Refund
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent data-testid={`admin-refund-dialog-${p.id}`}>
            <AlertDialogHeader>
              <AlertDialogTitle>Refund ${Number(p.amount).toFixed(2)} to {p.customer?.name || "this customer"}?</AlertDialogTitle>
              <AlertDialogDescription>The full amount goes back to the customer's card through Stripe. This can't be undone.</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid={`admin-refund-cancel-${p.id}`}>Keep payment</AlertDialogCancel>
              <AlertDialogAction data-testid={`admin-refund-confirm-${p.id}`} className="bg-rose-600 hover:bg-rose-700"
                onClick={() => act("refund", `/admin/entitlements/${p.id}/refund`, "Refunded")}>
                Yes, refund
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
      {["paid", "needs_refund"].includes(p.status) && !p.admin_comp && (
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button size="sm" variant="outline" data-testid={`admin-mark-refunded-${p.id}`} disabled={!!busy}
              className="mt-2 ml-2 h-8 rounded-full text-xs border-slate-300 text-slate-700">
              Mark as refunded
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent data-testid={`admin-mark-refunded-dialog-${p.id}`}>
            <AlertDialogHeader>
              <AlertDialogTitle>Already refunded in Stripe?</AlertDialogTitle>
              <AlertDialogDescription>Use this only if you refunded this payment yourself in your Stripe dashboard. It updates the record here; no money is moved.</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid={`admin-mark-refunded-cancel-${p.id}`}>Cancel</AlertDialogCancel>
              <AlertDialogAction data-testid={`admin-mark-refunded-confirm-${p.id}`}
                onClick={() => act("mark", `/admin/entitlements/${p.id}/mark-refunded`, "Marked as refunded")}>
                Mark as refunded
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
      {p.status === "refunded" && <div data-testid={`admin-refunded-${p.id}`} className="mt-2 text-xs text-slate-500">Refunded {p.refunded_at?.slice(0, 16).replace("T", " ")} UTC{p.refund_id === "manual" ? " (marked manually)" : ""}</div>}
      {p.refund_error && p.status !== "refunded" && <div className="mt-1 text-xs text-rose-700">Last refund attempt didn't go through in Stripe. Refund it in your Stripe dashboard, then tap "Mark as refunded".</div>}
    </div>
  );
}

export default function AdminPayments() {
  const [items, setItems] = useState([]);
  const reload = useCallback(() => call("/admin/purchases").then((d) => setItems(d.purchases || [])).catch(() => {}), []);
  useEffect(() => {
    reload();
  }, [reload]);
  return (
    <section className="mt-6 space-y-3" data-testid="admin-payments">
      <PricingSettings />
      <div className="font-display font-bold text-[#092C15]">Paid Concepts ({items.length})</div>
      {items.length === 0 ? <p className="text-sm text-slate-500">No paid concept purchases yet.</p> : items.map((p) => <Purchase key={p.id} p={p} reload={reload} />)}
    </section>
  );
}
